import { EditOutlined, LeftOutlined, MoreOutlined, ShareAltOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Collapse,
  Dropdown,
  Flex,
  type MenuProps,
  Pagination,
  type PaginationProps,
  Skeleton,
  Typography,
  message,
} from "antd";
import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import EmptyStateNote from "../../components/EmptyState/EmptyStateNote.component";
import GenderType from "../../components/Gender/GenderType.component";
import NotesList from "../../components/Notes/NotesList.component";
import PreWrapText from "../../components/PreWrapText/PreWrapText.component";
import ProgressSummaryView from "../../components/ProgressSummaryView/ProgressSummaryView.component";
import HowToTell from "../../components/Tutorial/HowToTell.component";
import Tutorial from "../../components/Tutorial/Tutorial.component";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView, useScrollToTop } from "../../hooks/ui-hook";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { deleteClient, getAllClients } from "../../service/client.service";
import {
  setCreateClientState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
  setShareClientModalClient,
  setShareClientModalVisible,
  setShowDemoSignupModal,
} from "../../slices/appStateSlice";
import { setClientForm, setClients, setIsEditing, setSharedClients } from "../../slices/clientSlice";
import { setNotesDetails, setRecordingId, setTotalNotes } from "../../slices/notesSlice";
import type { TClientForm, TNotes, Tdsm } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import {
  formatDistanceFromInputDateTimeToNow,
  getFirstLetterOfNameAndSurname,
  sortClientsByName,
} from "../../utils/datamanipulation.utils";
import { eventEmitter } from "../../utils/eventEmitter";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import Notes from "../notes/notes.component";
import styles from "./home.module.scss";

type TNotesSectionProps = {
  notes: TNotes[];
  setNotes: (notes: TNotes[]) => void;
  fetchNotes: (pageNumber?: number) => void;
  demoNotesRef?: React.RefObject<HTMLDivElement>;
};

const NotesSection = ({ notes, setNotes, fetchNotes, demoNotesRef }: TNotesSectionProps) => {
  const componentName = "home";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobileView = useIsMobileView();
  const scrollToTopNode = useScrollToTop();
  const { referIndividualBy } = useSpecialityReference();
  const { selectedClient, currentPage } = useAppSelector((state) => state.appState);
  const { rootPath } = useRootPath();
  const { totalNotes } = useAppSelector((state) => state.notes);
  const { filteredOwnedClients, filteredSharedClients } = useAppSelector((state) => state.client);
  const { isNotesViewVisible, loadingNotes, isDemoPath, isMobilePath } = useAppSelector((state) => state.appState);

  const showTutorial = totalNotes === 0 && !loadingNotes && !selectedClient;

  const showPastNotesHeader = selectedClient && !isNotesViewVisible;

  const [isProgressSummaryExpanded, setIsProgressSummaryExpanded] = useState(false);

  const handlePageChange: PaginationProps["onChange"] = (page) => {
    if (selectedClient) {
      fetchNotes(page);
    } else {
      fetchNotes(page);
    }
  };

  const navigateToNotesDetail = (item: TNotes) => {
    const updateNotes = notes.map((note) => {
      if (note.id === item.id) {
        return { ...note, is_read: true };
      }
      return note;
    });
    setNotes(updateNotes);
    if (item.error_status !== 0 || item.status !== 1) {
      return;
    }
    trackEvent(EventType.SELECT_NOTE, { recordingId: item.id });
    dispatch(setNotesDetails(item));
    dispatch(setLoadingNotesState(true));
    dispatch(setNotesViewVisible(true));
    dispatch(setRecordingId(item.id));
    scrollToTopNode();
  };

  const clearSelection = () => {
    dispatch(setLoadingNotesState(true));
    dispatch(setTotalNotes(0));
    dispatch(setSelectedClient(null));
    navigate(rootPath);
    eventEmitter.emit("refreshNotes");
  };

  const getDiagnostic = () => {
    if (!selectedClient?.diagnoses || (selectedClient.diagnoses && selectedClient.diagnoses.length === 0))
      return "No diagnosis added";
    return selectedClient.diagnoses.map((d: Tdsm) => `${d.name} (${d.code})`).join(", ");
  };

  const fetchClients = () => {
    getAllClients()
      .then((response) => {
        const ownedClients = sortClientsByName(response.data.owned || []);
        const shared = sortClientsByName(response.data.shared || []);
        dispatch(setClients([...ownedClients, ...shared]));
        dispatch(setSharedClients(shared));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleMenu = (e: any) => {
    if (e.key === "1") {
      dispatch(setCreateClientState(true));
      if (selectedClient) dispatch(setClientForm(selectedClient));
      dispatch(setIsEditing(true));
    }
    if (e.key === "2") {
      if (isDemoPath) {
        dispatch(setShowDemoSignupModal(true));
        return;
      }
      Swal.fire({
        title: `Would you like to delete this ${referIndividualBy.toLowerCase()}?`,
        text: `The ${referIndividualBy.toLowerCase()}'s notes will still be available unless you delete them.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, keep it",
      }).then((result) => {
        if (result.isConfirmed && selectedClient && selectedClient.id) {
          deleteClient(selectedClient.id)
            .then(() => {
              navigate(rootPath);
              fetchClients();
              dispatch(setSelectedClient(null));
              message.success(`${referIndividualBy} deleted successfully`);
            })
            .catch((error) => {
              console.error(error);
              message.error(`Failed to delete ${referIndividualBy.toLowerCase()}. Try Again later`);
            });
        }
      });
    }
    if (e.key === "3") {
      dispatch(setShareClientModalClient(selectedClient));
      dispatch(setShareClientModalVisible(true));
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "2",
      label: <span>Delete {referIndividualBy}</span>,
    },
  ];

  const editClient = () => {
    dispatch(setCreateClientState(true));
    if (selectedClient) dispatch(setClientForm(selectedClient));
    dispatch(setIsEditing(true));
  };

  const progressSummaryLastUpdatedOn = useMemo(() => {
    if (selectedClient?.progress_summary_last_updated_at) {
      return formatDistanceFromInputDateTimeToNow(selectedClient.progress_summary_last_updated_at);
    }
    return "";
  }, [selectedClient?.progress_summary_last_updated_at]);

  const toggleSummaryViewState = () => {
    if (!isProgressSummaryExpanded) {
      trackEvent(EventType.CLICKED_PROGRESS_SUMMARY_SHOW_MORE);
    } else {
      trackEvent(EventType.CLICKED_PROGRESS_SUMMARY_SHOW_LESS);
    }
    setIsProgressSummaryExpanded((prev) => !prev);
  };

  const renderProgressSummary = () => {
    if (!selectedClient || !selectedClient.progress_summary) {
      return `Progress summary is generated after you create a note. A progress summary gives you a snapshot of your ${referIndividualBy.toLowerCase()} based on past session notes. It is designed to help jog your memory and recommend next steps.`;
    }

    const summary = selectedClient.progress_summary;

    return (
      <>
        {isProgressSummaryExpanded ? (
          <>
            <ProgressSummaryView summary={summary} />
            <Button type="link" style={{ padding: 0 }} onClick={toggleSummaryViewState}>
              Show Less
            </Button>
          </>
        ) : (
          <>
            <div>
              <Typography
                style={{
                  fontWeight: "bold",
                  fontSize: "1rem",
                  margin: "0.5rem 0",
                }}
              >
                Primary Concern
              </Typography>
            </div>
            <div>
              <PreWrapText text={summary.primary_concern} />
            </div>
            <Button type="link" style={{ padding: 0 }} onClick={toggleSummaryViewState}>
              Show More
            </Button>
          </>
        )}
      </>
    );
  };

  const openShareModal = () => {
    const selectedClientData = [...filteredOwnedClients, ...filteredSharedClients].find(
      (client: TClientForm) => client.id === selectedClient.id,
    );
    dispatch(setShareClientModalClient(selectedClientData));
    dispatch(setShareClientModalVisible(true));
  };

  const CustomDropdown = () => {
    return (
      <Dropdown menu={{ items, onClick: handleMenu }} trigger={["click"]}>
        <svg
          style={{ cursor: "pointer" }}
          width="4"
          height="15"
          viewBox="0 0 4 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.66667 1.66732L1.66667 1.67565M1.66667 7.50065L1.66667 7.50898M1.66667 13.334L1.66667 13.3423M1.66667 2.50065C1.20643 2.50065 0.833333 2.12755 0.833333 1.66732C0.833333 1.20708 1.20643 0.833984 1.66667 0.833984C2.1269 0.833984 2.5 1.20708 2.5 1.66732C2.5 2.12755 2.1269 2.50065 1.66667 2.50065ZM1.66667 8.33398C1.20643 8.33398 0.833333 7.96089 0.833333 7.50065C0.833333 7.04041 1.20643 6.66732 1.66667 6.66732C2.1269 6.66732 2.5 7.04041 2.5 7.50065C2.5 7.96089 2.1269 8.33398 1.66667 8.33398ZM1.66667 14.1673C1.20643 14.1673 0.833333 13.7942 0.833333 13.334C0.833333 12.8737 1.20643 12.5007 1.66667 12.5007C2.1269 12.5007 2.5 12.8737 2.5 13.334C2.5 13.7942 2.1269 14.1673 1.66667 14.1673Z"
            stroke="#6366F1"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </Dropdown>
    );
  };

  if (showTutorial || isDemoPath) {
    return <Tutorial />;
  }

  if (isNotesViewVisible) {
    return <Notes />;
  }

  return (
    <>
      {selectedClient && (
        <>
          <Flex justify="space-between">
            <Button
              className={`${styles[`${componentName}-back`]} secondary-button`}
              onClick={clearSelection}
              icon={<LeftOutlined />}
            >
              Back
            </Button>
            {isMobileView && (
              <Flex gap={10} align="center">
                {!isMobilePath && (
                  <Button type="default" onClick={openShareModal} icon={<ShareAltOutlined />}>
                    Share
                  </Button>
                )}
                <Button type="primary" onClick={editClient} icon={<EditOutlined />}>
                  Edit
                </Button>
                <CustomDropdown />
              </Flex>
            )}
          </Flex>

          <Flex vertical gap={10} className={styles[`${componentName}__container-left-header-client-container`]}>
            {selectedClient ? (
              <>
                <Flex gap={10} align="center" justify="space-between">
                  <Flex align="center" gap={10}>
                    <Avatar
                      style={{
                        backgroundColor: getRandomAvatarColor(selectedClient.name),
                        minWidth: "24px",
                      }}
                      size={"small"}
                    >
                      {getFirstLetterOfNameAndSurname(selectedClient.name)}
                    </Avatar>
                    <Typography className={styles[`${componentName}__container-left-header-client`]}>
                      {selectedClient ? `${selectedClient.name}` : "Notes"}
                    </Typography>
                    <GenderType gender_type={selectedClient.gender_type} isBadge />
                  </Flex>
                  <Flex gap={10} align="center">
                    {!isMobileView && (
                      <Flex gap={10}>
                        <Button type="default" onClick={openShareModal} icon={<ShareAltOutlined />}>
                          Share
                        </Button>
                        <Button type="primary" onClick={editClient} icon={<EditOutlined />}>
                          Edit
                        </Button>
                      </Flex>
                    )}
                    {!isMobileView && <CustomDropdown />}
                  </Flex>
                </Flex>
                <Flex className={styles[`${componentName}__container-left-header-diagnosis`]} gap={5}>
                  <Typography
                    className={styles[`${componentName}__container-left-header-diagnosis-label`]}
                    style={{
                      minWidth: "80px",
                    }}
                  >
                    Diagnoses (ICD-10):
                  </Typography>
                  <Typography className={styles[`${componentName}__container-left-header-diagnosis-item`]}>
                    {getDiagnostic()}
                  </Typography>
                </Flex>
              </>
            ) : null}
          </Flex>
          <Collapse
            items={[
              {
                id: "special-card",
                key: "1",
                label: "Progress Summary",
                children: renderProgressSummary(),
                extra: progressSummaryLastUpdatedOn,
              },
            ]}
            defaultActiveKey={["1"]}
          />
        </>
      )}
      {loadingNotes ? (
        <>
          <Skeleton active />
          <Skeleton active />
        </>
      ) : (
        <div
          style={{
            overflowAnchor: "none",
          }}
        >
          {notes?.length ? (
            <>
              {showPastNotesHeader ? (
                <Flex className={styles[`${componentName}__container-left-header`]}>
                  <Typography className={styles[`${componentName}__container-left-header-title`]}>
                    Past Notes
                  </Typography>
                </Flex>
              ) : null}
              {notes.map((item, index) => (
                <NotesList
                  noteRef={demoNotesRef}
                  item={item}
                  key={item.id}
                  index={index}
                  navigateToNotesDetail={navigateToNotesDetail}
                  fetchNotes={fetchNotes}
                />
              ))}
            </>
          ) : (
            <>
              <EmptyStateNote />
              {!isMobilePath && <HowToTell />}
            </>
          )}
          {notes.length ? (
            <Pagination
              onChange={handlePageChange}
              current={currentPage}
              pageSize={50}
              total={totalNotes}
              showSizeChanger={false}
              style={{ textAlign: "center" }}
            />
          ) : null}
        </div>
      )}
    </>
  );
};

export default NotesSection;
